import {memoize} from 'lodash';

interface AbstractGetSyncPromiseOptions {
    onPromiseResolve: () => void;
    onPromiseReject: (error: Error) => void;
    onCall?: (input: any, output: SyncPromise<any>) => void;
}

export type SyncPromise<T> = [T, Error | undefined, boolean];

export type GetSyncPromise = <T extends {}, U = undefined>(
    input: T | Promise<T>,
    defaultOutputValue?: U
) => SyncPromise<T | U>;

const cache = new memoize.Cache();

// "getSyncPromise" is a utility that allows the value, errors, and loading state of a promise to be
// accessed synchronously. If the promise has been resolved, the resolved value is returned. If the
// promise is pending, a default value is returned.

// The abstract implementation is used in a React hook `useGetSyncPromise` and a test utility
// `testGetSyncPromise`.

// Example:
//    const promise = Promise.resolve('later');
//    getSyncPromise(promise, 'now') // ['now', undefined, false]
//    await promise;
//    getSyncPromise(promise, 'now') // ['later', undefined, true]

export default function abstractGetSyncPromise({
    onPromiseResolve,
    onPromiseReject,
    onCall,
}: AbstractGetSyncPromiseOptions): GetSyncPromise {

    return <T extends {}, U = undefined>(input: T | Promise<T>, defaultOutputValue?: U) => {
        let output: SyncPromise<T>;

        if (input instanceof Promise) {
            if (!cache.has(input)) {
                cache.set(input, [undefined, undefined, false]);
                detectThrashing();

                input.then(
                    (t: T) => {
                        cache.set(input, [t, undefined, true]);
                        onPromiseResolve();
                    },
                    (error: Error) => {
                        cache.set(input, [undefined, error, true]);
                        onPromiseReject(error);
                    }
                );
            }

            const [outputValue, error, isLoaded] = cache.get(input);
            output = [isLoaded ? outputValue : defaultOutputValue, error, isLoaded];
        } else {
            output = [input, undefined, true];
        }

        if (onCall) onCall(input, output);
        return output;
    };
}

let cacheCalls = 0;
let cacheSecond = 0;
let cacheWarning = false;
function detectThrashing() {
    if (process.env.NODE_ENV !== 'production') {
        const nextCacheSecond = Math.floor(Date.now() / 1000);
        if (cacheSecond !== nextCacheSecond) {
            if (cacheCalls > 30 && !cacheWarning) {
                // eslint-disable-next-line no-console
                console.trace(
                    'getSyncPromise appears to be thrashing. ' +
                    `It was called ${cacheCalls} times in the last second. ` +
                    'Check that promises are properly memoized'
                );
                cacheWarning = true;
            }
            cacheSecond = nextCacheSecond;
            cacheCalls = 0;
        }
        cacheCalls++;
    }
}
